/* eslint-disable max-lines-per-function */
import { ReactElement, useMemo } from 'react';

import { NumberPatternType } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   EmptyStatePlaceholder,
   InteractiveTable,
   InteractiveTableColumnConfiguration,
   InteractiveTableValidation,
   ValueRendererProps,
} from '@dreryk/react-components';

import { visitDetailsTranslationKeys } from '../../../../translations';
import { VisitFormBean, VisitFormController } from '../../visitForm.types';

import { ProcedureTableBean } from './procedures.types';
import { MAX_QUANTITY, MIN_QUANTITY, validProcedureCount } from './procedures.utils';

const procedureTableValidation: InteractiveTableValidation<ProcedureTableBean> = (value) => {
   return validProcedureCount(value) ? true : visitDetailsTranslationKeys.procedures.limitReached;
};

type ProcedureTableColumnConfiguration = InteractiveTableColumnConfiguration<ProcedureTableBean>;

const useColumnsConfiguration = (): ProcedureTableColumnConfiguration[] => {
   return useMemo(() => {
      const columnWidths = createDynamicColumnsWidth({
         code: '76px',
         name: '100%',
         quantity: '84px',
         options: '32px',
      });

      return [
         {
            name: 'code',
            label: visitDetailsTranslationKeys.procedures.icd9,
            width: columnWidths.code,

            renderer: ({ bean, index }: ValueRendererProps<ProcedureTableBean>) => {
               return (
                  <InteractiveTable.TextViewCell testId={'code'} name={'code'} index={index} defaultValue={bean.code} />
               );
            },
         },
         {
            name: 'name',

            renderer: ({ bean, index }: ValueRendererProps<ProcedureTableBean>) => {
               return (
                  <InteractiveTable.TextViewCell testId={'name'} name={'name'} index={index} defaultValue={bean.name} />
               );
            },
            label: visitDetailsTranslationKeys.procedures.procedureName,
            width: columnWidths.name,
         },
         {
            name: 'quantity',
            label: visitDetailsTranslationKeys.procedures.quantity,
            width: columnWidths.quantity,

            renderer: ({ bean, index }: ValueRendererProps<ProcedureTableBean>): JSX.Element | null => {
               return (
                  <>
                     <InteractiveTable.ControllerCell
                        testId={'minQuantity'}
                        name={'minQuantity'}
                        index={index}
                        defaultValue={bean.minQuantity}
                     />
                     <InteractiveTable.NumberInputCell
                        testId={'quantity'}
                        name={'quantity'}
                        index={index}
                        validationRules={{
                           required: true,
                        }}
                        defaultValue={bean.quantity}
                        pattern={NumberPatternType.POSITIVE_INTEGER}
                        enableControlButtons
                        stepValue={1}
                        min={bean.minQuantity === 0 ? MIN_QUANTITY : bean.minQuantity}
                        max={MAX_QUANTITY}
                     />
                  </>
               );
            },
         },
         {
            name: 'options',
            width: columnWidths.options,

            renderer: ({ bean, index }: ValueRendererProps<ProcedureTableBean>): JSX.Element | null => {
               return <InteractiveTable.DeleteRowCell index={index} disabled={bean.minQuantity > 0} />;
            },
         },
      ];
   }, []);
};

type ProceduresTableProps = Pick<VisitFormController, 'register'> & {
   readonly selector: ReactElement;
};

export const ProceduresTable = (props: ProceduresTableProps): JSX.Element => {
   const columns = useColumnsConfiguration();

   return (
      <InteractiveTable<ProcedureTableBean, VisitFormBean>
         name={'procedures.value'}
         register={props.register}
         validation={procedureTableValidation}
         columns={columns}
         testId={'procedures'}
         header={props.selector}
         placeholder={
            <EmptyStatePlaceholder
               heading={visitDetailsTranslationKeys.procedures.noResultsLabel}
               subheading={visitDetailsTranslationKeys.procedures.noResultsSubText}
               fullLength
               testId={'noResults'}
            />
         }
         autoScroll={'start'}
      />
   );
};
